import { NotAuthenticatedError, ValidationError } from '@/auth/api';
import { useAuth } from '@/hooks/useAuth';
import { createFileRoute, Link, Navigate } from '@tanstack/react-router';
import { Button } from '@ui/components/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ui/components/card';
import { Field, FieldDescription, FieldGroup, FieldSeparator } from '@ui/components/field';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@ui/components/form';
import { Input } from '@ui/components/input';
import { useForm } from 'react-hook-form';

export const Route = createFileRoute('/_auth/sign-in')({
  component: RouteComponent,
  loader: () => ({
    crumb: 'Sign In',
  }),
});

interface SignInRequest {
  email: string;
  password: string;
}

function RouteComponent() {
  const methods = useForm<SignInRequest>();
  const { signIn, useSession } = useAuth();
  const { data: session } = useSession();

  if (session) {
    return <Navigate to="/dashboard" />;
  }

  const onSubmit = async (data: SignInRequest) => {
    try {
      const { error } = await signIn.email(data);
      if (error) {
        throw error;
      }
    } catch (err) {
      if (err instanceof ValidationError) {
        err.errors.forEach((error) => {
          console.error('error', error);
          methods.setError(error.field.replaceAll('body.', '') as keyof SignInRequest, {
            message: error.message,
          });
        });
      } else if (err instanceof NotAuthenticatedError) {
        methods.setError('email', { message: 'Invalid email or password' });
        methods.setError('password', {
          message: 'Invalid email or password',
        });
      } else {
        methods.setError('email', { message: 'An unexpected error occurred' });
        methods.setError('password', {
          message: 'An unexpected error occurred',
        });
      }
    }
  };

  return (
    <div className="bg-background flex flex-col items-center justify-center gap-6 align-middle h-full">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-xl">Welcome back</CardTitle>
          <CardDescription>Login with your Google account</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)} className="space-y-4">
              <FieldGroup>
                <Field>
                  <Button
                    variant="outline"
                    type="button"
                    onClick={() =>
                      signIn.social({
                        provider: 'google',
                      })
                    }
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 48 48"
                      width="24px"
                      height="24px"
                    >
                      <path
                        fill="#FFC107"
                        d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12s5.373-12,12-12 c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24s8.955,20,20,20 c10.234,0,18.756-7.438,19.861-17.178C44.634,22.843,44.634,21.45,43.611,20.083z"
                      />
                      <path
                        fill="#FF3D00"
                        d="M6.306,14.691l6.571,4.819C14.655,16.108,19.006,14,24,14c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657 C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
                      />
                      <path
                        fill="#4CAF50"
                        d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36 c-5.202,0-9.619-3.317-11.283-8.065l-6.522,5.025C10.286,39.556,16.227,44,24,44z"
                      />
                      <path
                        fill="#1976D2"
                        d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.002,0.003-0.003 l6.19,5.238C36.971,39.205,44,34,44,24C44,22.843,44.634,21.45,43.611,20.083z"
                      />
                    </svg>
                    Login with Google
                  </Button>
                </Field>
                <FieldSeparator className="*:data-[slot=field-separator-content]:bg-card">
                  Or continue with
                </FieldSeparator>

                <FormField
                  control={methods.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email:</FormLabel>
                      <FormControl>
                        <Input placeholder="youremail@email.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={methods.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password:</FormLabel>
                      <FormControl>
                        <Input type="password" placeholder="password" {...field} />
                      </FormControl>
                      <FormMessage />
                      <a href="#" className="ml-auto text-sm underline-offset-4 hover:underline">
                        Forgot your password?
                      </a>
                    </FormItem>
                  )}
                />
                <Field>
                  <Button type="submit">Login</Button>
                  <FieldDescription className="text-center">
                    Don&apos;t have an account?{' '}
                    <Link to="/register" className="text-blue-500">
                      Sign Up
                    </Link>
                  </FieldDescription>
                </Field>
              </FieldGroup>
            </form>
          </Form>
        </CardContent>
      </Card>
      <FieldDescription className="px-6 text-center">
        By clicking continue, you agree to our <a href="#">Terms of Service</a> and{' '}
        <a href="#">Privacy Policy</a>.
      </FieldDescription>
    </div>
  );
}
